import axios from 'axios';
import { isTokenExpired } from '../utils/tokenUtils';
import { getRestriction, restrictionMessage } from '../utils/restrictions';
import i18n from '../i18n';
import toast from './toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5166/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  withCredentials: true
});

const isAuthRefreshUrl = (url) =>
    url?.includes('/auth/refresh') || url?.includes('/auth/logout');

let refreshPromise = null;

// Single-flight refresh: concurrent callers share the same in-flight request
const refreshAccessToken = () => {
  if (!refreshPromise) {
    refreshPromise = api.post('/auth/refresh', {}, { withCredentials: true })
        .then(({ data }) => {
          localStorage.setItem('token', data.token);
          window.dispatchEvent(new CustomEvent('auth:tokenRefreshed', { detail: { token: data.token } }));
          return data.token;
        })
        .catch((err) => {
          localStorage.removeItem('token');
          window.dispatchEvent(new Event('auth:logout'));
          throw err;
        })
        .finally(() => {
          refreshPromise = null;
        });
  }
  return refreshPromise;
};

api.interceptors.request.use(async (config) => {
  if (isAuthRefreshUrl(config.url)) {
    return config;
  }

  let token = localStorage.getItem('token');
  // [AllowAnonymous] endpoints silently ignore an expired token instead of
  // returning 401, so the response interceptor below never fires for them.
  if (token && isTokenExpired(token)) {
    try {
      token = await refreshAccessToken();
    } catch {
      token = null;
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

let lastBanNoticeAt = 0;

// A banned user is signed out; parallel failing requests share one notice.
const handleBan = (error, restriction) => {
  error.restrictionHandled = true;
  if (Date.now() - lastBanNoticeAt < 3000) return;
  lastBanNoticeAt = Date.now();
  toast.error(restrictionMessage(i18n.t.bind(i18n), restriction));
  window.dispatchEvent(new Event('auth:logout'));
};

api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;

      const restriction = getRestriction(error);
      if (restriction?.code === 'user_banned') handleBan(error, restriction);

      if (error.response?.status !== 401 || original._retry || isAuthRefreshUrl(original.url)) {
        return Promise.reject(error);
      }

      original._retry = true;

      try {
        const newToken = await refreshAccessToken();
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      } catch (refreshError) {
        // Another tab may have already refreshed — check localStorage
        const latestToken = localStorage.getItem('token');
        const originalToken = error.config.headers?.Authorization?.replace('Bearer ', '');
        if (latestToken && latestToken !== originalToken) {
          original.headers.Authorization = `Bearer ${latestToken}`;
          return api(original);
        }
        return Promise.reject(refreshError);
      }
    }
);


export const authService = {
  login: (username, password) =>
      api.post('/Auth/login', { username, password }),

  refresh: () => api.post('/auth/refresh', {}, { withCredentials: true }),
  logout: () => api.post('/auth/logout', {}, { withCredentials: true }),

  register: (username, email, password) =>
      api.post('/Auth/register', { username, email, password }),
  googleLogin: (idToken) => api.post('/auth/google', { idToken }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (userId, token, newPassword) =>
      api.post('/auth/reset-password', { userId, token, newPassword }),
  resendConfirmation: (email) => api.post('/auth/resend-confirmation', { email }),
};

export const profileService = {
  getMyProfile: () => api.get('/UserProfile/me'),

  getUserPosts: (userId, sortBy = 'new') =>
      api.get(`/UserProfile/by-id/${userId}/posts`, { params: { sortBy } }),

  getUserWallPosts: (userId, sortBy = 'new') =>
      api.get(`/UserProfile/by-id/${userId}/wall`, { params: { sortBy } }),

  getUserComments: (userId, sortBy = 'new') =>
      api.get(`/UserProfile/by-id/${userId}/comments`, { params: { sortBy } }),

  getProfile: (username) => api.get(`/UserProfile/${username}`),

  getProfileById: (userId) => api.get(`/UserProfile/by-id/${userId}`),

  getUserActivity: (userId) =>
      api.get(`/UserProfile/by-id/${userId}/activity`),

  getUserAchievements: (userId) =>
      api.get(`/UserProfile/by-id/${userId}/achievements`),

  updateProfile: (bio, displayName) =>
      api.put('/UserProfile/update', { bio, displayName }),

  uploadAvatar: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/UserProfile/upload-avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  uploadCover: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/UserProfile/upload-cover', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const postsService = {
  getPost: (categorySlug, postId) =>
      api.get(`/Posts/${categorySlug}/${postId}`),

  getWallPost: (userId, postId) =>
      api.get(`/Posts/user/${userId}/${postId}`),

  incrementView: (postId) =>
      api.post(`/Posts/${postId}/view`),

  ratePost: (postId, type) =>
      api.post(`/Posts/${postId}/rating`, { type }),

  getSimilarPosts: (postId) =>
      api.get(`/Posts/${postId}/similar`),
};

export const updatesService = {
  getUpdates: (page = 1, pageSize = 20) =>
      api.get('/Updates', { params: { page, pageSize } }),

  getUpdate: (slug) =>
      api.get(`/Updates/${slug}`),

  incrementView: (updateId) =>
      api.post(`/Updates/${updateId}/view`),

  rateUpdate: (updateId, type) =>
      api.post(`/Updates/${updateId}/rating`, { type }),

  checkUnreadUpdates: () => api.get('/Updates/unread'),

  markUpdatesRead: () => api.post('/Updates/read'),
};

export const updateCommentsService = {
  getComments: (updateId, page = 1, pageSize = 10, sort = null) =>
      api.get(`/updates/${updateId}/comments`, { params: { page, pageSize, sortBy: sort } }),

  createComment: (updateId, content, image = null, parentCommentId = null) => {
    const formData = new FormData();
    if (content) formData.append('content', content);
    if (parentCommentId) formData.append('parentCommentId', parentCommentId);
    if (image) formData.append('image', image);

    return api.post(`/updates/${updateId}/comments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  rateComment: (updateId, commentId, type) =>
      api.post(`/updates/${updateId}/comments/${commentId}/rating`, { type })
};

export const commentsService = {
getComments: (postId, page = 1, pageSize = 10, sort = null) =>
      api.get(`/posts/${postId}/comments`, { params: { page, pageSize, sortBy: sort } }),

  createComment: (postId, content, image = null, parentCommentId = null) => {
    const formData = new FormData();
    if (content) formData.append('content', content);
    if (parentCommentId) formData.append('parentCommentId', parentCommentId);
    if (image) formData.append('image', image);

    return api.post(`/posts/${postId}/comments`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  rateComment: (postId, commentId, type) =>
      api.post(`/posts/${postId}/comments/${commentId}/rating`, { type })
};

export const subscriptionsService = {
  subscribeToUser: (userId) =>
      api.post(`/Subscriptions/users/${userId}`),

  unsubscribeFromUser: (userId) =>
      api.delete(`/Subscriptions/users/${userId}`),

  subscribeToCategory: (categoryId) =>
      api.post(`/Subscriptions/categories/${categoryId}`),

  unsubscribeFromCategory: (categoryId) =>
      api.delete(`/Subscriptions/categories/${categoryId}`),

  getSubscriptionFeed: (sortBy = 'new', page = 1, pageSize = 20) =>
      api.get('/Subscriptions/feed', { params: { sortBy, page, pageSize } }),

  checkUserSubscription: (userId) =>
      api.get(`/Subscriptions/check/user/${userId}`),

  checkCategorySubscription: (categoryId) =>
      api.get(`/Subscriptions/check/category/${categoryId}`),

  getFollowers: (userId) =>
      api.get(`/Subscriptions/users/${userId}/followers`),

  getFollowingUsers: (userId) =>
      api.get(`/Subscriptions/users/${userId}/following/users`),

  getFollowingCategories: (userId) =>
      api.get(`/Subscriptions/users/${userId}/following/categories`),
};

export const chatService = {
  getChats: () => api.get('/Chats'),
  getChat: (chatId) => api.get(`/Chats/${chatId}`),
  createChat: (otherUserId) =>
      api.post('/Chats', { otherUserId, isGroup: false }),
  deleteChat: (chatId) => api.delete(`/Chats/${chatId}`),
  getMessages: (chatId, page = 1, pageSize = 50) =>
      api.get(`/Chats/${chatId}/Messages`, { params: { page, pageSize } }),
  sendMessage: (chatId, content, image = null) => {
    const formData = new FormData();
    formData.append('content', content);
    if (image) formData.append('image', image);
    return api.post(`/Chats/${chatId}/Messages`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  markAsRead: (chatId) => api.post(`/Chats/${chatId}/Messages/read`),
  deleteMessage: (chatId, messageId) =>
      api.delete(`/Chats/${chatId}/Messages/${messageId}`),
  editMessage: (chatId, messageId, content) =>
      api.put(`/Chats/${chatId}/Messages/${messageId}`, { content })
};

export const reportsService = {
  create: (targetType, targetId, reason, comment = null) =>
      api.post('/Reports', { targetType, targetId, reason, comment }),
};

export const userHidesService = {
  getAll: () => api.get('/UserHides'),
  get: (userId) => api.get(`/UserHides/${userId}`),
  set: (userId, hidePosts, hideComments) => api.put(`/UserHides/${userId}`, { hidePosts, hideComments }),
};

export const complaintsService = {
  create: (subject, text) => api.post('/Complaints', { subject: subject || null, text }),
};

export const notificationService = {
  getList: (before = null, take = 20) =>
      api.get('/Notifications', { params: { before, take } }),
  getUnreadCount: () => api.get('/Notifications/unread-count'),
  markRead: (id) => api.post(`/Notifications/${id}/read`),
  markAllRead: () => api.post('/Notifications/read-all'),
  getSettings: () => api.get('/Notifications/settings'),
  updateSettings: (settings) => api.put('/Notifications/settings', settings),
};

export const searchService = {
  searchUsers: (q) => api.get('/Search/users', { params: { q } }),
};

export const karmaService = {
  getTopUsers: () => api.get('/karma/top'),
  recalculate: () => api.post('/karma/recalculate')
};

export default api;
